/**
 * Portal-Seite: Probestunden-Anfrage einreichen + eigene Anfragen sehen.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { CalendarPlus, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import PortalLayout from './PortalLayout';
import api from '@/lib/axios';
import { formatDate } from '@/lib/utils';
import { statusLabel } from '@/lib/deLabels';

const STATUS_LABEL = {
  new: { label: 'Neu eingegangen', class: 'bg-blue-100 text-blue-700' },
  contacted: { label: 'Kontaktiert', class: 'bg-amber-100 text-amber-700' },
  scheduled: { label: 'Geplant', class: 'bg-emerald-100 text-emerald-700' },
  declined: { label: 'Abgelehnt', class: 'bg-slate-200 text-slate-700' },
};

export default function PortalTrialLessonPage() {
  const [form, setForm] = useState({
    student_first_name: '', student_last_name: '', student_age: '',
    subject_preference: '', experience: '', location_preference: '',
    preferred_times: ['', '', ''], guardian_phone: '',
  });
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/portal/self-service/trial-lessons');
      setList(r.data);
    } catch {
      // ignore
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.student_first_name || !form.student_last_name) {
      toast.error('Bitte Vor- und Nachname des Kindes angeben');
      return;
    }
    setBusy(true);
    try {
      await api.post('/portal/self-service/trial-lessons', {
        ...form,
        student_age: form.student_age ? parseInt(form.student_age, 10) : null,
        preferred_times: form.preferred_times.filter((t) => t.trim()),
      });
      toast.success('Probestunden-Anfrage eingereicht — die Verwaltung meldet sich.');
      setForm({
        student_first_name: '', student_last_name: '', student_age: '',
        subject_preference: '', experience: '', location_preference: '',
        preferred_times: ['', '', ''], guardian_phone: '',
      });
      load();
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Anfrage fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const setTime = (i, v) => {
    const next = [...form.preferred_times];
    next[i] = v;
    setForm({ ...form, preferred_times: next });
  };

  return (
    <PortalLayout>
      <div className="space-y-6" data-testid="portal-trial-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarPlus className="w-6 h-6 text-brand-red" /> Probestunde anfragen
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Unverbindliche Anfrage. Die Musikschule meldet sich mit Terminvorschlägen.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Neue Anfrage</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Vorname Kind *</Label>
                <Input value={form.student_first_name} onChange={(e) => setForm({ ...form, student_first_name: e.target.value })} data-testid="trial-firstname" />
              </div>
              <div>
                <Label>Nachname Kind *</Label>
                <Input value={form.student_last_name} onChange={(e) => setForm({ ...form, student_last_name: e.target.value })} data-testid="trial-lastname" />
              </div>
              <div>
                <Label>Alter (optional)</Label>
                <Input type="number" min="2" max="120" value={form.student_age} onChange={(e) => setForm({ ...form, student_age: e.target.value })} />
              </div>
              <div>
                <Label>Instrument / Fach</Label>
                <Input value={form.subject_preference} onChange={(e) => setForm({ ...form, subject_preference: e.target.value })} placeholder="z. B. Klavier, Gitarre, Gesang" data-testid="trial-subject" />
              </div>
              <div className="md:col-span-2">
                <Label>Vorerfahrung (optional)</Label>
                <Textarea rows={2} value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} placeholder="z. B. seit 2 Jahren Blockflöte, Anfänger:in, …" />
              </div>
              <div>
                <Label>Bevorzugter Standort</Label>
                <Input value={form.location_preference} onChange={(e) => setForm({ ...form, location_preference: e.target.value })} placeholder="z. B. Hauptgebäude / Filiale" />
              </div>
              <div>
                <Label>Telefon (für Rückruf)</Label>
                <Input value={form.guardian_phone} onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Wunschtermine (bis zu 3)</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-1">
                  {form.preferred_times.map((t, i) => (
                    <Input
                      key={i}
                      value={t}
                      onChange={(e) => setTime(i, e.target.value)}
                      placeholder={`Termin ${i + 1} (z. B. Mi 16-18 Uhr)`}
                      data-testid={`trial-time-${i}`}
                    />
                  ))}
                </div>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" disabled={busy} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="trial-submit">
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                  Anfrage einreichen
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Meine Anfragen</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin" /> Laden…</p>
            ) : list.length === 0 ? (
              <p className="text-slate-400 text-sm">Noch keine Anfragen eingereicht.</p>
            ) : (
              <div className="space-y-2">
                {list.map((r) => (
                  <div key={r.id} className="border border-slate-200 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{r.student_first_name} {r.student_last_name}{r.subject_preference ? ` — ${r.subject_preference}` : ''}</p>
                      <p className="text-xs text-slate-500">eingereicht {formatDate(r.created_at)}</p>
                      {r.admin_notes && <p className="text-xs text-slate-600 mt-1">Verwaltung: {r.admin_notes}</p>}
                    </div>
                    <Badge className={STATUS_LABEL[r.status]?.class || ''}>
                      {STATUS_LABEL[r.status]?.label || statusLabel(r.status)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
